import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { useToast } from "@/hooks/use-toast";
import ProfitStrip from "@/components/expenses/ProfitStrip";
import ExpenseList from "@/components/expenses/ExpenseList";
import AddExpenseDrawer from "@/components/expenses/AddExpenseDrawer";
import RecurringSection from "@/components/expenses/RecurringSection";
import VisibilityPanel from "@/components/expenses/VisibilityPanel";

const PERIOD_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7 Days" },
  { value: "mtd", label: "Month to Date" },
  { value: "30d", label: "Last 30 Days" },
];

export default function Expenses() {
  const { activeClinic } = useAuth();
  const { branches, activeBranch, activeBranchId, loading: branchLoading } = useBranch();
  const { toast } = useToast();

  const [period, setPeriod] = useState("mtd");
  const branchId = activeBranchId || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    if (!branchId) {
      setData(null);
      setError(branchLoading ? "" : "No active branch selected.");
      setLoading(Boolean(branchLoading));
      return;
    }
    const { data: res, error: rpcError } = await supabase.rpc("expenses_dashboard", {
      p_period: period,
      p_branch_id: branchId,
    });
    if (rpcError) {
      setError(rpcError.message || "Could not load expenses.");
      setData(null);
    } else {
      setError("");
      setData(res || null);
    }
    setLoading(false);
  }, [period, branchId, branchLoading]);

  useEffect(() => { load(); }, [load]);

  const branchOptions = useMemo(() => {
    const entries = Array.isArray(data?.entries) ? data.entries : [];
    const byId = new Map();
    for (const branch of Array.isArray(branches) ? branches : []) {
      if (branch?.id && branch?.name && !byId.has(branch.id)) {
        byId.set(branch.id, { id: branch.id, name: branch.name });
      }
    }
    for (const entry of entries) {
      if (entry.branch_id && entry.branch_name && !byId.has(entry.branch_id)) {
        byId.set(entry.branch_id, { id: entry.branch_id, name: entry.branch_name });
      }
    }
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [branches, data]);

  const branchSelectOptions = useMemo(() => {
    if (!branchId) return branchOptions;
    const selected = activeBranch?.id === branchId
      ? { id: activeBranch.id, name: activeBranch.name }
      : branchOptions.find((branch) => branch.id === branchId);
    return selected ? [selected] : [];
  }, [activeBranch, branchId, branchOptions]);

  const handleVoid = useCallback(async (expenseId, reason) => {
    const { data: voidResult, error: rpcError } = await supabase.rpc("void_clinic_expense", {
      p_expense_id: expenseId,
      p_reason: reason,
    });
    if (rpcError) {
      toast({
        title: "Couldn't void the expense",
        description: rpcError.message || "Something went wrong. Try again.",
        variant: "destructive",
      });
      // Re-throw so the dialog keeps itself open / re-enabled.
      throw rpcError;
    }
    toast({
      title: "Expense voided",
      description: voidResult?.will_not_repost
        ? "This recurring/lab entry will not be re-posted for this period."
        : undefined,
    });
    await load();
  }, [load, toast]);

  if (loading && !data) {
    return (
      <div data-testid="expenses-loading" className="p-5 text-[13px] text-slate-500">
        Loading expenses…
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="expenses-error" className="p-5 text-[13px] text-rose-700">
        {error}
      </div>
    );
  }

  const totals = data?.totals || {};
  const currency = data?.currency || activeClinic?.currency || "PHP";
  const canViewSensitive = !!data?.can_view_sensitive;
  const entries = Array.isArray(data?.entries) ? data.entries : [];
  const categories = Array.isArray(data?.categories) ? data.categories : [];
  const periodLabel = data?.period_label || "Month to date";

  return (
    <main data-testid="expenses-page" className="mx-auto max-w-[1180px] space-y-5 p-5 text-slate-950">
      <header className="space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-[13px] font-semibold text-slate-500">
              {activeClinic?.clinic_name || activeClinic?.name || "Clinic"} · {periodLabel}
            </p>
            <h1 className="mt-1 text-[30px] font-bold tracking-tight text-slate-950">Expenses</h1>
          </div>
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <select
              data-testid="expenses-period-select"
              value={period}
              onChange={(event) => setPeriod(event.target.value)}
              className="h-10 rounded-md border border-slate-300 bg-white px-3 text-[13px] font-semibold text-slate-700 outline-none transition hover:bg-slate-50 focus:border-teal-600 focus:ring-2 focus:ring-teal-100"
            >
              {PERIOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <button
              type="button"
              data-testid="expenses-add-button"
              onClick={() => setDrawerOpen(true)}
              className="inline-flex h-10 items-center gap-2 rounded-md bg-teal-600 px-4 text-[13px] font-semibold text-white shadow-sm transition hover:bg-teal-700"
            >
              <Plus className="h-4 w-4" strokeWidth={2.5} />
              Add expense
            </button>
          </div>
        </div>
      </header>

      <ProfitStrip totals={totals} currency={currency} canViewSensitive={canViewSensitive} />

      <ExpenseList
        entries={entries}
        restrictedCount={totals.restricted_count}
        canViewSensitive={canViewSensitive}
        currency={currency}
        onVoid={handleVoid}
      />

      {data?.has_more ? (
        <p className="text-[12px] font-medium text-slate-500">
          Showing the most recent 500 entries.
        </p>
      ) : null}

      {/* Task 9 — Recurring section, first-visit overhead suggestions, and the
          owner-only sensitive-visibility panel. */}
      <RecurringSection
        recurring={data?.recurring || []}
        categories={categories}
        branches={branchSelectOptions}
        suggestions={data?.suggestions || []}
        currency={currency}
        onChanged={load}
      />

      {data?.is_sensitive_admin && data?.visibility ? (
        <VisibilityPanel visibility={data.visibility} onChanged={load} />
      ) : null}

      <AddExpenseDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        categories={categories}
        branches={branchSelectOptions}
        currency={currency}
        onSaved={() => {
          setDrawerOpen(false);
          load();
        }}
      />
    </main>
  );
}
